(() => {
  'use strict';

  let toastTimer = 0;

  function getToast() {
    let toast = document.querySelector('[data-ckc-cart-feedback]');
    if (toast) return toast;

    toast = document.createElement('div');
    toast.className = 'ckc-cart-feedback';
    toast.hidden = true;
    toast.setAttribute('data-ckc-cart-feedback', '');
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');

    const message = document.createElement('span');
    message.className = 'ckc-cart-feedback__message';
    message.setAttribute('data-ckc-cart-feedback-message', '');

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'ckc-cart-feedback__close';
    close.setAttribute('aria-label', 'Dismiss cart message');
    close.textContent = '×';
    close.addEventListener('click', () => {
      toast.hidden = true;
      toast.classList.remove('is-visible');
    });

    toast.append(message, close);
    document.body.append(toast);
    return toast;
  }

  function showToast(message) {
    const toast = getToast();
    const messageNode = toast.querySelector('[data-ckc-cart-feedback-message]');
    if (messageNode) messageNode.textContent = message;
    toast.hidden = false;
    toast.classList.remove('is-visible');
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => {
      toast.hidden = true;
      toast.classList.remove('is-visible');
    }, 5000);
  }

  function setFormError(form, message) {
    const wrapper = form?.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    const target = wrapper?.querySelector('.product-form__error-message');
    if (wrapper && target) {
      target.textContent = message;
      wrapper.hidden = false;
    } else {
      showToast(message);
    }
  }

  function normalizeQuantity(value, input) {
    const min = Math.max(1, Number(input?.min || input?.dataset.min || 1));
    const step = Math.max(1, Number(input?.step || input?.dataset.step || 1));
    const maxValue = Number(input?.max || input?.dataset.max || 0);
    const max = maxValue > 0 ? maxValue : Number.POSITIVE_INFINITY;
    const parsed = Number.parseInt(value, 10);
    const requested = Number.isFinite(parsed) ? parsed : min;
    const stepped = min + Math.round((requested - min) / step) * step;
    return Math.min(max, Math.max(min, stepped));
  }

  function setInputValue(input, value) {
    if (!input || input.value === String(value)) return;
    input.value = String(value);
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setAttributeIfChanged(element, name, value) {
    const next = String(value);
    if (element && element.getAttribute(name) !== next) element.setAttribute(name, next);
  }

  function updateCartBubbleFromMarkup(markup) {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon || !markup) return false;

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const nextBubble = parsed.querySelector('.cart-count-bubble');
    const currentBubble = icon.querySelector('.cart-count-bubble');

    if (!nextBubble) {
      currentBubble?.remove();
      return true;
    }

    const clone = nextBubble.cloneNode(true);
    if (currentBubble) currentBubble.replaceWith(clone);
    else icon.append(clone);
    return true;
  }

  function updateCartBubbleCount(count) {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    const itemCount = Math.max(0, Number(count) || 0);
    let bubble = icon.querySelector('.cart-count-bubble');
    if (itemCount === 0) {
      bubble?.remove();
      return;
    }

    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      const visible = document.createElement('span');
      visible.setAttribute('aria-hidden', 'true');
      const accessible = document.createElement('span');
      accessible.className = 'visually-hidden';
      bubble.append(visible, accessible);
      icon.append(bubble);
    }

    const visible = bubble.querySelector('[aria-hidden="true"]');
    if (visible) visible.textContent = itemCount < 100 ? String(itemCount) : '';
    const accessible = bubble.querySelector('.visually-hidden');
    if (accessible) accessible.textContent = `${itemCount} ${itemCount === 1 ? 'item' : 'items'} in cart`;
  }

  function ensureCartDrawerAccessibility() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    const cartTitle = drawer.querySelector('.drawer__heading')?.textContent.trim() || 'Cart';
    drawer.querySelectorAll('.drawer__close').forEach((button) => {
      button.type = 'button';
      const currentLabel = button.getAttribute('aria-label')?.trim();
      if (!currentLabel || currentLabel.toLowerCase() === 'close') {
        setAttributeIfChanged(button, 'aria-label', `${currentLabel || 'Close'} ${cartTitle}`);
      }
      setAttributeIfChanged(button, 'aria-controls', 'CartDrawer');
    });

    if (drawer.dataset.ckcA11yBound === 'true') return;
    drawer.dataset.ckcA11yBound = 'true';
    const cartTrigger = document.querySelector('#cart-icon-bubble');
    const syncExpandedState = () => {
      if (cartTrigger) cartTrigger.setAttribute('aria-expanded', String(drawer.classList.contains('active')));
    };
    syncExpandedState();
    new MutationObserver(syncExpandedState).observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  function closeCartDrawerOnEscape(event) {
    if (event.key !== 'Escape') return;
    const drawer = document.querySelector('cart-drawer.active');
    if (!drawer) return;
    if (event.target instanceof Element && event.target.closest('details[open]')) return;
    event.preventDefault();
    event.stopPropagation();
    drawer.close();
  }

  function updateCartSections(data) {
    const drawer = document.querySelector('cart-drawer');
    const drawerMarkup = data?.sections?.['cart-drawer'];
    if (drawer && drawerMarkup) {
      const parsed = new DOMParser().parseFromString(drawerMarkup, 'text/html');
      const nextDrawer = parsed.querySelector('#CartDrawer');
      const currentDrawer = drawer.querySelector('#CartDrawer');
      if (nextDrawer && currentDrawer) {
        currentDrawer.innerHTML = nextDrawer.innerHTML;
        const isEmpty = !nextDrawer.querySelector('.cart-item');
        drawer.classList.toggle('is-empty', isEmpty);
        drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', isEmpty);
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close(), { once: true });
        ensureCartDrawerAccessibility();
      }
    }

    const hasBubbleMarkup = updateCartBubbleFromMarkup(data?.sections?.['cart-icon-bubble']);
    if (!hasBubbleMarkup) {
      const cartUrl = window.routes?.cart_url || '/cart';
      fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
        .then((response) => response.ok ? response.json() : null)
        .then((cart) => { if (cart) updateCartBubbleCount(cart.item_count); })
        .catch(() => {});
    }

    try {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'ckc-storefront-polish',
          productVariantId: String(data?.id || ''),
          cartData: data,
        });
      }
    } catch (_) {
      // The cart response is already applied; an unavailable theme event bus is non-fatal.
    }
  }

  function makeAddUrl() {
    const route = window.routes?.cart_add_url || '/cart/add';
    const url = new URL(route, window.location.origin);
    url.pathname = url.pathname.replace(/\/+$/, '');
    if (!url.pathname.endsWith('.js')) url.pathname += '.js';
    url.searchParams.set('sections', 'cart-drawer,cart-icon-bubble');
    url.searchParams.set('sections_url', window.location.pathname);
    return url;
  }

  async function addFormToCart(form, quantity, triggerButton) {
    const variantInput = form?.querySelector('[name="id"]');
    if (!variantInput?.value || variantInput.disabled) {
      throw new Error('Choose an available option before adding this item.');
    }
    if (triggerButton?.disabled || triggerButton?.getAttribute('aria-disabled') === 'true') {
      throw new Error('This option is currently unavailable.');
    }

    const formSubmit = form.querySelector('button[type="submit"][name="add"], button[type="submit"]');
    if ((formSubmit?.disabled && formSubmit?.getAttribute('aria-busy') !== 'true') || formSubmit?.getAttribute('aria-disabled') === 'true') {
      throw new Error('This option is currently unavailable.');
    }

    const body = new FormData(form);
    body.set('id', String(variantInput.value));
    if (quantity != null) body.set('quantity', String(quantity));

    const response = await fetch(makeAddUrl(), {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body,
      credentials: 'same-origin',
    });

    let data = {};
    try {
      data = await response.json();
    } catch (_) {
      throw new Error('We could not confirm the cart update. Please try again.');
    }

    if (!response.ok) {
      throw new Error(data.description || data.message || 'We could not add this item. Please try again.');
    }

    updateCartSections(data);
    return data;
  }

  function initStickyAtc(sticky) {
    if (sticky.dataset.ckcInitialized === 'true') return;
    sticky.dataset.ckcInitialized = 'true';

    const sectionId = sticky.dataset.sectionId;
    const productTitle = sticky.dataset.productTitle || 'This item';
    const productInfo = document.getElementById(`ProductInfo-${sectionId}`);
    const productForm = document.getElementById(`product-form-${sectionId}`);
    const originalButton = document.getElementById(`ProductSubmitButton-${sectionId}`);
    const stickyButton = sticky.querySelector('[data-ckc-sticky-submit]');
    const stickyLabel = sticky.querySelector('[data-ckc-sticky-label]');
    const stickyQuantity = sticky.querySelector('[data-ckc-sticky-quantity]');
    const variantLabel = sticky.querySelector('[data-ckc-sticky-variant]');
    const status = sticky.querySelector('[data-ckc-sticky-status]');
    if (!productForm || !originalButton || !stickyButton || !stickyQuantity) return;

    let variants = [];
    try {
      variants = JSON.parse(sticky.dataset.variants || '[]');
    } catch (_) {
      variants = [];
    }

    // Stay hidden until the first observer callback confirms the main CTA has left view.
    let mainVisible = true;
    let footerVisible = false;
    let adding = false;
    const variantInput = productForm.querySelector('[name="id"]');
    let quantityField =
      document.getElementById(`Quantity-${sectionId}`) ||
      productForm.querySelector('[name="quantity"]');

    if (!quantityField) {
      quantityField = document.createElement('input');
      quantityField.type = 'hidden';
      quantityField.name = 'quantity';
      quantityField.value = '1';
      productForm.append(quantityField);
    }

    const currentVariant = () => variants.find((variant) => String(variant.id) === String(variantInput?.value));
    const stockAction = () => document.querySelector('.restock-rocket-button');
    const isVisible = (element) =>
      Boolean(element && element.getClientRects().length && getComputedStyle(element).visibility !== 'hidden');

    const updateQuantityRules = () => {
      const selected = currentVariant();
      const min = Number(selected?.quantity_rule?.min || quantityField.min || 1);
      const step = Number(selected?.quantity_rule?.increment || quantityField.step || 1);
      const max = Number(selected?.quantity_rule?.max || quantityField.max || 0);
      if (min > 0) {
        setAttributeIfChanged(stickyQuantity, 'min', min);
        setAttributeIfChanged(quantityField, 'min', min);
      }
      if (step > 0) {
        setAttributeIfChanged(stickyQuantity, 'step', step);
        setAttributeIfChanged(quantityField, 'step', step);
      }
      if (max > 0) {
        setAttributeIfChanged(stickyQuantity, 'max', max);
        setAttributeIfChanged(quantityField, 'max', max);
      } else {
        if (stickyQuantity.hasAttribute('max')) stickyQuantity.removeAttribute('max');
        if (quantityField.hasAttribute('max')) quantityField.removeAttribute('max');
      }
    };

    const syncQuantity = () => {
      updateQuantityRules();
      const quantity = normalizeQuantity(quantityField.value || stickyQuantity.value, quantityField);
      stickyQuantity.value = String(quantity);
      setInputValue(quantityField, quantity);
    };

    const isSoldOut = () => {
      const selected = currentVariant();
      if (selected) return selected.available === false;
      return originalButton.disabled &&
        originalButton.querySelector('span')?.textContent.trim() === sticky.dataset.soldOutLabel;
    };

    const updateVisibility = (soldOut, hasRestock) => {
      const drawerOpen = document.querySelector('cart-drawer')?.classList.contains('active') || false;
      const shouldShow = !mainVisible && !footerVisible && !drawerOpen && (!soldOut || hasRestock);
      sticky.hidden = !shouldShow;
      sticky.setAttribute('aria-hidden', String(!shouldShow));
      sticky.inert = !shouldShow;
      document.documentElement.classList.toggle('ckc-sticky-atc-active', shouldShow);
      if (shouldShow) {
        requestAnimationFrame(() => {
          if (!sticky.hidden) {
            document.documentElement.style.setProperty('--ckc-sticky-atc-height', `${sticky.getBoundingClientRect().height}px`);
          }
        });
      }
    };

    const sync = () => {
      const selected = currentVariant();
      const soldOut = isSoldOut();
      const restockButton = soldOut ? stockAction() : null;
      const hasRestock = isVisible(restockButton);
      const variantName = selected?.title && selected.title !== 'Default Title' ? selected.title : '';
      if (variantLabel) {
        variantLabel.textContent = variantName;
        variantLabel.hidden = !variantName;
      }

      syncQuantity();
      stickyQuantity.disabled = soldOut && !hasRestock;
      const label = hasRestock
        ? 'Notify me when available'
        : originalButton.querySelector('span')?.textContent.trim() ||
          (soldOut ? sticky.dataset.soldOutLabel : sticky.dataset.addLabel);
      if (stickyLabel && stickyLabel.textContent.trim() !== label) stickyLabel.textContent = label;

      const unavailable = soldOut || !selected || originalButton.disabled;
      stickyButton.disabled = adding || (unavailable && !hasRestock);
      stickyButton.setAttribute('aria-disabled', String(stickyButton.disabled));
      stickyButton.setAttribute(
        'aria-label',
        `${label} ${productTitle}${variantName ? `, ${variantName}` : ''}`
      );
      updateVisibility(soldOut, hasRestock);
    };

    const mainObserver = new IntersectionObserver((entries) => {
      mainVisible = entries[0]?.isIntersecting || false;
      updateVisibility(isSoldOut(), Boolean(isVisible(stockAction())));
    }, { threshold: 0 });
    mainObserver.observe(originalButton);

    const footer = document.querySelector('footer, .footer');
    const footerObserver = footer
      ? new IntersectionObserver((entries) => {
          footerVisible = entries[0]?.isIntersecting || false;
          updateVisibility(isSoldOut(), Boolean(isVisible(stockAction())));
        }, { threshold: 0.01, rootMargin: '0px 0px -8% 0px' })
      : null;
    if (footer && footerObserver) footerObserver.observe(footer);

    const observedProduct = productInfo || productForm;
    const observerOptions = { attributes: true, childList: true, characterData: true, subtree: true };
    let syncFrame = 0;
    const productObserver = new MutationObserver(() => {
      if (syncFrame) return;
      syncFrame = requestAnimationFrame(() => {
        syncFrame = 0;
        productObserver.disconnect();
        try { sync(); } finally {
          if (observedProduct.isConnected) productObserver.observe(observedProduct, observerOptions);
        }
      });
    });
    productObserver.observe(observedProduct, observerOptions);

    const drawer = document.querySelector('cart-drawer');
    const drawerObserver = drawer
      ? new MutationObserver(sync)
      : null;
    if (drawer && drawerObserver) drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    productInfo?.addEventListener('change', sync);
    productInfo?.addEventListener('input', (event) => {
      if (event.target?.name === 'quantity') sync();
    });

    stickyQuantity.addEventListener('change', syncQuantity);
    stickyQuantity.addEventListener('input', () => {
      if (stickyQuantity.value !== '') setInputValue(quantityField, normalizeQuantity(stickyQuantity.value, quantityField));
    });

    sticky.querySelectorAll('[data-ckc-qty-step]').forEach((button) => {
      button.addEventListener('click', () => {
        if (stickyQuantity.disabled) return;
        const step = Number(stickyQuantity.step || 1);
        const current = normalizeQuantity(stickyQuantity.value, quantityField);
        const direction = Number(button.dataset.ckcQtyStep) < 0 ? -1 : 1;
        const next = normalizeQuantity(current + direction * step, quantityField);
        stickyQuantity.value = String(next);
        setInputValue(quantityField, next);
        stickyQuantity.focus({ preventScroll: true });
      });
    });

    stickyButton.addEventListener('click', async () => {
      sync();
      if (stickyButton.disabled) return;
      if (isSoldOut()) {
        stockAction()?.click();
        return;
      }

      if (!variantInput?.value || variantInput.disabled || originalButton.disabled) {
        if (status) status.textContent = 'Choose an available option before adding this item.';
        return;
      }

      if (!productForm.reportValidity()) return;
      syncQuantity();
      adding = true;
      stickyButton.disabled = true;
      stickyButton.setAttribute('aria-busy', 'true');
      if (status) status.textContent = '';

      try {
        const quantity = normalizeQuantity(stickyQuantity.value, quantityField);
        const data = await addFormToCart(productForm, quantity, originalButton);
        if (status) status.textContent = 'Added to cart.';
        stickyButton.classList.add('is-added');
        window.setTimeout(() => stickyButton.classList.remove('is-added'), 800);
        showToast(`${productTitle} added to your cart.`);
      } catch (error) {
        if (status) status.textContent = error.message || 'We could not add this item. Please try again.';
      } finally {
        adding = false;
        stickyButton.removeAttribute('aria-busy');
        sync();
      }
    });

    sync();

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.detail?.sectionId !== sectionId) return;
      mainObserver.disconnect();
      footerObserver?.disconnect();
      productObserver.disconnect();
      cancelAnimationFrame(syncFrame);
      drawerObserver?.disconnect();
      document.documentElement.classList.remove('ckc-sticky-atc-active');
    });
  }

  function handleQuickAddSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-type="add-to-cart-form"]')) return;

    const quickAddModal = form.closest('quick-add-modal');
    const quickAddControl = form.closest('.quick-add');
    if (!quickAddModal && !quickAddControl) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!form.reportValidity()) return;
    const button = event.submitter || form.querySelector('button[type="submit"][name="add"], button[type="submit"]');
    if (button?.disabled) return;

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const titleNode = quickAddModal?.querySelector('.product__title h1, .product__title a') ||
      form.closest('.card-wrapper')?.querySelector('.card__heading');
    const title = titleNode?.textContent.trim() || 'Item';

    addFormToCart(form, null, null)
      .then(() => {
        quickAddModal?.hide?.(true);
        showToast(`${title} added to your cart.`);
      })
      .catch((error) => setFormError(form, error.message || 'We could not add this item. Please try again.'))
      .finally(() => {
        if (button?.isConnected) {
          button.removeAttribute('aria-busy');
          button.disabled = false;
        }
      });
  }

  function handleProductSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[data-type="add-to-cart-form"]')) return;
    if (!form.closest('product-info') || form.closest('quick-add-modal') || form.closest('.quick-add')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (form.dataset.ckcSubmitting === 'true' || !form.reportValidity()) return;

    const button = event.submitter || form.querySelector('button[type="submit"][name="add"], button[type="submit"]');
    if (button?.disabled) return;
    const title = form.closest('product-info')?.querySelector('.product__title h1')?.textContent.trim() || 'Item';
    form.dataset.ckcSubmitting = 'true';
    button?.setAttribute('aria-busy', 'true');

    addFormToCart(form, null, button)
      .then(() => {
        if (button?.isConnected) {
          button.classList.add('is-added');
          window.setTimeout(() => button.classList.remove('is-added'), 800);
        }
        showToast(`${title} added to your cart.`);
      })
      .catch((error) => setFormError(form, error.message || 'We could not add this item. Please try again.'))
      .finally(() => {
        delete form.dataset.ckcSubmitting;
        button?.removeAttribute('aria-busy');
      });
  }


  function initScrollReveals() {
    if (window.Shopify?.designMode || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;
    const sections = Array.from(document.querySelectorAll('#MainContent > .shopify-section'));
    const targets = [];
    const itemSelector = '.grid__item, .collection-list__item, .multicolumn-list__item, .rich-text__blocks > *, .image-with-text__text-item';
    sections.forEach((section, sectionIndex) => {
      const top = section.getBoundingClientRect().top;
      if (sectionIndex === 0 || top < window.innerHeight * 0.78) return;
      if (section.querySelector('img[fetchpriority="high"], img[loading="eager"]')) return;
      const items = Array.from(section.querySelectorAll(itemSelector));
      if (items.length) {
        items.forEach((item, index) => {
          item.style.setProperty('--ckc-reveal-delay', `${Math.min(index, 5) * 65}ms`);
          targets.push(item);
        });
      } else {
        section.style.setProperty('--ckc-reveal-delay', '0ms');
        targets.push(section);
      }
    });
    if (!targets.length) return;
    const observer = new IntersectionObserver((entries, currentObserver) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-ckc-revealed');
        currentObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -6% 0px', threshold: 0.08 });
    targets.forEach((target) => {
      target.dataset.ckcReveal = '';
      observer.observe(target);
    });
  }

  function isCkcTransitionPath(pathname) {
    return pathname === '/' || /^\/(?:collections|products)(?:\/|$)/.test(pathname);
  }

  function initCkcPageTransitions() {
    if (window.Shopify?.designMode || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let navigating = false;
    window.addEventListener('pageshow', () => {
      navigating = false;
      document.documentElement.classList.remove('ckc-page-leaving');
    });
    document.addEventListener('click', (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const link = event.target.closest?.('a[href]');
      if (!link || link.hasAttribute('download')) return;
      const target = link.getAttribute('target');
      if (target && target !== '_self') return;
      const destination = new URL(link.href, window.location.href);
      if (destination.origin !== window.location.origin || destination.href === window.location.href) return;
      if (!isCkcTransitionPath(window.location.pathname) || !isCkcTransitionPath(destination.pathname)) return;
      if (navigating) { event.preventDefault(); return; }
      navigating = true;
      event.preventDefault();
      document.documentElement.classList.add('ckc-page-leaving');
      window.setTimeout(() => window.location.assign(destination.href), 150);
    });
  }

  function initialize() {
    initScrollReveals();
    initCkcPageTransitions();
    document.querySelectorAll('[data-ckc-sticky-atc]').forEach(initStickyAtc);
    ensureCartDrawerAccessibility();
    document.addEventListener('keyup', closeCartDrawerOnEscape, true);
    document.addEventListener('submit', handleProductSubmit, true);
    document.addEventListener('submit', handleQuickAddSubmit, true);

    const observer = new MutationObserver(() => {
      ensureCartDrawerAccessibility();
      document.querySelectorAll('[data-ckc-sticky-atc]').forEach(initStickyAtc);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:unload', (event) => {
      if (!event.detail?.sectionId) return;
      const stillPresent = document.querySelector('[data-ckc-sticky-atc]');
      if (!stillPresent) observer.disconnect();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
